import React, { useState } from 'react';
import { ChevronLeft, Plus, User, Mail, Phone, Star, StickyNote, AlertTriangle, MapPin } from 'lucide-react';
import type { Database } from '../../lib/database.types';
import { useCustomerBio, useCustomerContacts, useCustomerNotes } from '../../lib/hooks';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ContactItem } from './ContactItem';
import { CustomerNotes } from './CustomerNotes';
import { QuickActionsMenu } from './QuickActionsMenu';
import AddressManager from '../../pages/customers/AddressManager';

type Customer = Database['public']['Tables']['customers']['Row'];

interface CustomerProfileProps {
  customer: Customer;
  onBack: () => void;
}

export const CustomerProfile: React.FC<CustomerProfileProps> = ({
  customer,
  onBack,
}) => {
  const { bio } = useCustomerBio(
    customer.first_name,
    customer.last_name,
    customer.city,
    customer.state
  );
  const { contacts, loading: contactsLoading, addContact, updateContact, deleteContact } = useCustomerContacts(customer.id);
  const { notes, addNote, deleteNote } = useCustomerNotes(customer.id);
  const [showAddressManager, setShowAddressManager] = useState(false);

  if (showAddressManager) {
    return (
      <AddressManager 
        customerId={customer.id}
        onBack={() => setShowAddressManager(false)}
      />
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={onBack}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {customer.first_name} {customer.last_name}
            </h1>
            <p className="text-muted-foreground">Customer Profile</p>
          </div>
        </div>
        
        <QuickActionsMenu
          contacts={contacts}
          onAddContact={addContact}
          onAddNote={addNote}
          onCreateWorkOrder={() => {
            window.dispatchEvent(new CustomEvent('routeChange', { detail: 'work-orders' }));
          }}
        />
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="all" className="w-full">
                <div className="flex justify-between items-center mb-4">
                  <TabsList>
                    <TabsTrigger value="all">All Contacts</TabsTrigger>
                    <TabsTrigger value="addresses">Addresses</TabsTrigger>
                    <TabsTrigger value="phones">Phone Numbers</TabsTrigger>
                    <TabsTrigger value="emails">Email Addresses</TabsTrigger>
                  </TabsList>
                  <Button onClick={() => setShowAddressManager(true)}>
                    <MapPin className="w-4 h-4 mr-2" />
                    Manage Addresses
                  </Button>
                </div>

                <TabsContent value="all" className="space-y-4">
                  {contacts.map((contact) => (
                    <ContactItem
                      key={contact.id}
                      contact={contact}
                      onUpdate={updateContact}
                      onDelete={deleteContact}
                    />
                  ))}
                </TabsContent>
                <TabsContent value="addresses" className="space-y-4">
                  {contacts
                    .filter((c) => c.type === 'address')
                    .map((contact) => (
                      <ContactItem
                        key={contact.id}
                        contact={contact}
                        onUpdate={updateContact}
                        onDelete={deleteContact}
                      />
                    ))}
                </TabsContent>
                <TabsContent value="phones" className="space-y-4">
                  {contacts
                    .filter((c) => c.type === 'phone')
                    .map((contact) => (
                      <ContactItem
                        key={contact.id}
                        contact={contact}
                        onUpdate={updateContact}
                        onDelete={deleteContact}
                      />
                    ))}
                </TabsContent>
                <TabsContent value="emails" className="space-y-4">
                  {contacts
                    .filter((c) => c.type === 'email')
                    .map((contact) => (
                      <ContactItem
                        key={contact.id}
                        contact={contact}
                        onUpdate={updateContact}
                        onDelete={deleteContact}
                      />
                    ))}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <CustomerNotes 
            notes={notes}
            onAddNote={addNote}
            onDeleteNote={deleteNote}
          />
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Customer Bio</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{bio}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};